import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { AlertCircle, BarChart3, FileText, Globe, Loader2, Lock, Mail, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Login() {
  const { user, loading, login, loginMicrosoft, msalEnabled } = useAuth();
  const { t, rtl, toggle } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [msBusy, setMsBusy] = useState(false);

  if (!loading && user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email.trim(), password);
      navigate("/");
    } catch (err) {
      setError(apiError(err, t("login.fail")));
    } finally {
      setBusy(false);
    }
  };

  const msLogin = async () => {
    setError("");
    setMsBusy(true);
    try {
      await loginMicrosoft();
      navigate("/");
    } catch (err) {
      setError(apiError(err, t("login.msFail")));
    } finally {
      setMsBusy(false);
    }
  };

  return (
    <div className="relative grid min-h-screen lg:grid-cols-2" data-testid="login-page">
      <button
        data-testid="language-toggle-button"
        onClick={toggle}
        className={`absolute top-4 z-10 flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:border-delta-limeBorder hover:text-delta-navy lg:border-white/30 lg:bg-white/10 lg:text-white lg:hover:bg-white/20 ${rtl ? "left-4" : "right-4"}`}
      >
        <Globe className="h-3.5 w-3.5" />
        {rtl ? "English" : "العربية"}
      </button>
      <div className="delta-pattern relative hidden flex-col justify-between bg-delta-navy p-10 lg:flex">
        <div className="rounded-xl bg-white p-3 shadow-lg" style={{ width: "fit-content" }}>
          <img src="/assets/delta-logo.png" alt="Delta Medical Laboratories" className="h-14 w-auto object-contain" />
        </div>
        <div className="space-y-6">
          <h1 className="font-heading text-4xl font-bold leading-tight text-white">
            {t("login.hero1")}<br />{t("login.hero2")}
          </h1>
          <p className="max-w-md text-base leading-relaxed text-white/70">
            {t("login.heroText")}
          </p>
          <div className="space-y-3">
            {[
              { icon: FileText, text: t("login.feat1") },
              { icon: ShieldCheck, text: t("login.feat2") },
              { icon: BarChart3, text: t("login.feat3") },
            ].map((f) => (
              <div key={f.text} className="flex items-center gap-3 text-sm text-white/80">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-delta-lime/20">
                  <f.icon className="h-4 w-4 text-delta-lime" />
                </div>
                {f.text}
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-white/40">{t("login.brandFooter")}</p>
      </div>

      <div className="flex items-center justify-center bg-background p-6">
        <div className="w-full max-w-md animate-fade-up space-y-6">
          <div className="lg:hidden">
            <div className="inline-block rounded-xl bg-white p-2 shadow-sm">
              <img src="/assets/delta-logo.png" alt="Delta Medical Laboratories" className="h-12 w-auto object-contain" />
            </div>
          </div>
          <div>
            <h2 className="font-heading text-2xl font-bold text-slate-900 sm:text-3xl">{t("login.welcome")}</h2>
            <p className="mt-1 text-sm text-slate-500">{t("login.subtitle")}</p>
          </div>

          <Button
            type="button"
            variant="outline"
            data-testid="microsoft-sso-button"
            disabled={!msalEnabled || msBusy}
            onClick={msLogin}
            className="h-11 w-full border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            title={msalEnabled ? t("login.msTooltipOn") : t("login.msTooltipOff")}
          >
            {msBusy ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : (
              <svg className="me-2 h-4 w-4" viewBox="0 0 21 21" fill="none">
                <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
              </svg>
            )}
            {t("login.msButton")}
          </Button>
          {!msalEnabled && (
            <p className="-mt-4 text-center text-[11px] text-slate-400" data-testid="sso-pending-note">
              {t("login.msPending")}
            </p>
          )}

          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-xs text-slate-400">{t("login.orEmail")}</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            {error && (
              <div data-testid="login-error" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                {error}
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">{t("login.email")}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  required
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@deltalab.com"
                  className="h-11 pl-9"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">{t("login.password")}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  required
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-11 pl-9"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit-button"
              className="h-11 w-full bg-delta-navy text-sm font-semibold hover:bg-delta-navyDark"
            >
              {busy && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
              {t("login.signIn")}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
